/**
 * AdminRaffleTab — manages settings.raffle. Content saves via the dashboard's
 * main SAVE button (same config PUT as everything else). Entries/winner are
 * live data pulled from /api/admin/raffle. Reset is a confirm-guarded action
 * that only clears the raffle window + winner (orders untouched).
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, RefreshCw, Trophy, AlertTriangle, ShoppingBag } from "lucide-react";
import { fetchAdminRaffle, resetRaffle } from "@/api";
import LatestReleaseTebexPicker from "@/components/LatestReleaseTebexPicker";

const Input = ({ label, value, onChange, placeholder, testid, type = "text", rows }) => (
  <div>
    <label className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] block mb-2">
      &gt; {label}
    </label>
    {rows ? (
      <textarea
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        className="w-full bg-black border border-[var(--line-strong)] focus:border-[var(--accent)] outline-none px-3 py-2 text-sm text-white"
      />
    ) : (
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-black border border-[var(--line-strong)] focus:border-[var(--accent)] outline-none px-3 py-2 text-sm text-white font-mono"
      />
    )}
  </div>
);

const ListEditor = ({ label, items, onChange, placeholder, testid, addLabel }) => (
  <div>
    <div className="flex items-center justify-between mb-2">
      <label className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)]">
        &gt; {label} ({items.length})
      </label>
      <button
        data-testid={`${testid}-add`}
        onClick={() => onChange([...items, ""])}
        className="font-mono text-[10px] uppercase tracking-widest text-[var(--accent)] hover:underline flex items-center gap-1"
      >
        <Plus size={10} /> {addLabel || "ADD"}
      </button>
    </div>
    <div className="space-y-2">
      {items.map((v, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="font-mono text-[10px] text-[var(--accent)] w-6">{String(i + 1).padStart(2, "0")}</span>
          <input
            data-testid={`${testid}-${i}`}
            value={v}
            onChange={(e) => {
              const next = [...items];
              next[i] = e.target.value;
              onChange(next);
            }}
            placeholder={placeholder}
            className="flex-1 bg-black border border-[var(--line-strong)] focus:border-[var(--accent)] outline-none px-3 py-2 text-sm text-white"
          />
          <button
            onClick={() => onChange(items.filter((_, k) => k !== i))}
            className="border border-[var(--line-strong)] hover:border-red-500 hover:text-red-500 p-2"
            aria-label="remove"
          >
            <Trash2 size={12} />
          </button>
        </div>
      ))}
    </div>
  </div>
);

const isoToLocal = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const p = (x) => String(x).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};
const localToIso = (v) => {
  if (!v) return "";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString();
};

const STATUS_OPTIONS = [
  { value: "hidden", label: "Hidden (tab not shown)" },
  { value: "active", label: "Active (LIVE — entries open)" },
  { value: "paused", label: "Paused (visible, entries closed)" },
  { value: "completed", label: "Completed (winner screen)" },
];

export default function AdminRaffleTab({ config, updateSetting }) {
  const raffle = config.settings.raffle || {};
  const up = (patch) => updateSetting({ raffle: { ...raffle, ...patch } });

  const [live, setLive] = useState(null);
  const [liveLoading, setLiveLoading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const refreshLive = useCallback(async () => {
    setLiveLoading(true);
    try {
      setLive(await fetchAdminRaffle());
    } catch (_) {
      toast.error("Failed to load raffle entries");
    } finally {
      setLiveLoading(false);
    }
  }, []);
  useEffect(() => { refreshLive(); }, [refreshLive]);

  const handleReset = async () => {
    if (!window.confirm("Reset the raffle? This clears the previous entries window and the saved winner. Orders, products and customers are NOT affected.")) return;
    try {
      const res = await resetRaffle();
      updateSetting({
        raffle: {
          ...raffle,
          raffle_id: res.raffle_id,
          entries_since: res.entries_since,
          status: "hidden",
        },
      });
      toast.success("Raffle reset — previous entries & winner cleared");
      refreshLive();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    }
  };

  return (
    <section className="space-y-6 max-w-3xl" data-testid="panel-raffle">
      <h2 className="font-display text-2xl font-extrabold uppercase tracking-tight mb-2">Raffle</h2>

      {/* Status */}
      <div className="bg-[#0a0a0a] border border-[var(--line-strong)] p-6 space-y-5">
        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] block mb-2">
            &gt; Status
          </label>
          <select
            data-testid="raffle-status-select"
            value={raffle.status || "hidden"}
            onChange={(e) => up({ status: e.target.value })}
            className="w-full bg-black border border-[var(--line-strong)] focus:border-[var(--accent)] outline-none px-3 py-2 text-sm text-white font-mono"
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <p className="font-mono text-[9px] text-[var(--muted)] uppercase tracking-widest mt-2 leading-relaxed">
            // Active shows the RAFFLE tab with a red LIVE badge. The status flips to Completed automatically when the draw runs.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <Input
            label="Start date & time (your local time)"
            type="datetime-local"
            value={isoToLocal(raffle.start_at)}
            onChange={(v) => up({ start_at: localToIso(v) })}
            testid="raffle-start-at"
          />
          <Input
            label="Draw date & time (your local time)"
            type="datetime-local"
            value={isoToLocal(raffle.draw_at)}
            onChange={(v) => up({ draw_at: localToIso(v) })}
            testid="raffle-draw-at"
          />
        </div>
      </div>

      {/* Content */}
      <div className="bg-[#0a0a0a] border border-[var(--line-strong)] p-6 space-y-5">
        <Input label="Raffle title" value={raffle.title || ""} onChange={(v) => up({ title: v })} testid="raffle-title-input" />
        <Input label="Raffle description" rows={2} value={raffle.description || ""} onChange={(v) => up({ description: v })} testid="raffle-description-input" />
        <Input label="Prize title" value={raffle.prize_title || ""} onChange={(v) => up({ prize_title: v })} testid="raffle-prize-title-input" />
        <Input label="Prize description" rows={3} value={raffle.prize_description || ""} onChange={(v) => up({ prize_description: v })} testid="raffle-prize-description-input" />
        <div className="grid md:grid-cols-2 gap-4">
          <Input label="Prize value (e.g. $200+)" value={raffle.prize_value || ""} onChange={(v) => up({ prize_value: v })} testid="raffle-prize-value-input" />
          <Input label="Entry price (display, e.g. $5.00)" value={raffle.entry_price || ""} onChange={(v) => up({ entry_price: v })} testid="raffle-entry-price-input" />
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] block mb-2">
            &gt; Tebex raffle package
          </label>
          <div className="flex items-center gap-2 flex-wrap">
            {raffle.tebex_package_id ? (
              <div
                className="flex-1 min-w-0 bg-black border border-[var(--accent)]/40 px-3 py-2 text-sm text-white font-mono flex items-center gap-2"
                data-testid="raffle-package-linked"
              >
                <span className="text-[var(--accent)]">ID:</span>
                <span className="truncate">{raffle.tebex_package_id}</span>
              </div>
            ) : (
              <div
                className="flex-1 min-w-0 bg-black border border-[var(--line-strong)] px-3 py-2 text-sm text-[var(--muted)] font-mono"
                data-testid="raffle-package-empty"
              >
                No package linked yet
              </div>
            )}
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              data-testid="raffle-sync-package-btn"
              className="border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)] hover:text-black px-3 py-2 font-mono text-[10px] uppercase tracking-widest transition inline-flex items-center gap-2"
            >
              <ShoppingBag size={12} /> SYNC FROM TEBEX
            </button>
            {raffle.tebex_package_id && (
              <button
                type="button"
                onClick={() => up({ tebex_package_id: "" })}
                data-testid="raffle-package-clear"
                className="border border-[var(--line-strong)] text-[var(--muted)] hover:border-red-500 hover:text-red-400 px-3 py-2 font-mono text-[10px] uppercase tracking-widest transition"
              >
                CLEAR
              </button>
            )}
          </div>
          <p className="font-mono text-[9px] text-[var(--muted)] uppercase tracking-widest mt-2 leading-relaxed">
            // Click SYNC FROM TEBEX to pick a package from your storefront. Every paid purchase of this package = 1 entry.
          </p>
        </div>
        <ListEditor
          label="Prize images (URLs)"
          items={raffle.prize_images || []}
          onChange={(prize_images) => up({ prize_images })}
          placeholder="https://i.imgur.com/..."
          testid="raffle-image"
          addLabel="ADD IMAGE"
        />
        <ListEditor
          label="Included prize features"
          items={raffle.prize_features || []}
          onChange={(prize_features) => up({ prize_features })}
          placeholder="Custom engine sound"
          testid="raffle-feature"
          addLabel="ADD FEATURE"
        />
        <ListEditor
          label="Raffle rules"
          items={raffle.rules || []}
          onChange={(rules) => up({ rules })}
          placeholder="One winner will be drawn at the scheduled time..."
          testid="raffle-rule"
          addLabel="ADD RULE"
        />
        <p className="font-mono text-[9px] text-[var(--muted)] uppercase tracking-widest leading-relaxed">
          // Remember to hit SAVE (top-right) to publish changes.
        </p>
      </div>

      {/* Live entries + winner */}
      <div className="bg-[#0a0a0a] border border-[var(--line-strong)] p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)]">
            &gt; Live entries {live ? `(${live.total_entries} valid)` : ""}
          </div>
          <button
            data-testid="raffle-refresh-entries"
            onClick={refreshLive}
            disabled={liveLoading}
            className="font-mono text-[10px] uppercase tracking-widest text-[var(--accent)] hover:underline flex items-center gap-1 disabled:opacity-50"
          >
            <RefreshCw size={10} className={liveLoading ? "animate-spin" : ""} /> REFRESH
          </button>
        </div>

        {live?.winner && (
          <div className="border border-[#e6b84c]/50 bg-[#e6b84c]/5 p-4 rounded" data-testid="raffle-admin-winner">
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-[#e6b84c] mb-2">
              <Trophy size={12} /> WINNER DRAWN
            </div>
            <div className="font-display text-xl uppercase">{live.winner.winner_username || "(no valid entries)"}</div>
            <div className="font-mono text-[10px] text-[var(--muted)] mt-1">
              {live.winner.total_entries} entries · drawn {live.winner.drawn_at ? new Date(live.winner.drawn_at).toLocaleString() : "—"}
              {live.winner.winner_order_id ? ` · order ${live.winner.winner_order_id.slice(0, 8)}` : ""}
            </div>
          </div>
        )}

        {live && live.entries.length > 0 ? (
          <div className="max-h-72 overflow-y-auto border border-[var(--line)]">
            <table className="w-full text-left">
              <thead className="sticky top-0 bg-black">
                <tr className="font-mono text-[9px] uppercase tracking-widest text-[var(--muted)]">
                  <th className="px-3 py-2">#</th>
                  <th className="px-3 py-2">Username</th>
                  <th className="px-3 py-2">Order</th>
                  <th className="px-3 py-2">Tx</th>
                </tr>
              </thead>
              <tbody>
                {live.entries.map((e, i) => (
                  <tr key={i} data-testid={`raffle-admin-entry-${i}`} className="border-t border-[var(--line)] font-mono text-[11px]">
                    <td className="px-3 py-1.5 text-[var(--muted)]">{i + 1}</td>
                    <td className="px-3 py-1.5 text-white">{e.username}</td>
                    <td className="px-3 py-1.5 text-[var(--muted)]">{(e.order_id || "").slice(0, 8)}</td>
                    <td className="px-3 py-1.5 text-[var(--muted)]">{e.tx_id || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)]">
            // No valid entries yet — entries appear when a Tebex payment for the raffle package completes
          </p>
        )}
      </div>

      {/* Reset */}
      <div className="border border-red-500/30 bg-red-500/5 p-5 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <AlertTriangle size={16} className="text-red-400 mt-0.5" />
          <p className="font-mono text-[10px] text-[var(--muted)] uppercase tracking-widest leading-relaxed max-w-md">
            Reset clears the previous raffle&apos;s entries window + saved winner so you can run a new one. Orders, products & customers are untouched.
          </p>
        </div>
        <button
          data-testid="raffle-reset-btn"
          onClick={handleReset}
          className="border border-red-500/40 text-red-400 hover:bg-red-500 hover:text-black px-4 py-2 font-mono text-xs uppercase tracking-widest transition"
        >
          RESET RAFFLE
        </button>
      </div>

      {/* Tebex package picker */}
      <LatestReleaseTebexPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        currentId={raffle.tebex_package_id || ""}
        testIdPrefix="raffle-tebex-picker"
        title="Link Raffle Package"
        onPick={(pkg) => {
          up({
            tebex_package_id: String(pkg.id),
            // Only auto-fill empty fields so we don't clobber admin edits
            entry_price: raffle.entry_price || pkg.price || "",
            prize_title: raffle.prize_title || pkg.name || raffle.prize_title || "",
          });
          toast.success(`Linked Tebex package: ${pkg.name || pkg.id}`);
        }}
      />
    </section>
  );
}
